import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  InputSignal,
  ModelSignal,
  OnInit,
  Optional,
  OutputEmitterRef,
  Signal,
  TemplateRef,
  WritableSignal,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';

import {
  InsightsBaseEvent,
  InsightsEvent,
  InsightsEventProperties,
  InsightsEventsTrackingService,
  TypedTemplateDirective,
  insightsNamedEvent,
} from '@core/shared/util';
import {
  FilterBadgeComponent,
  FilterClearButtonComponent,
  FilterGroupComponent,
  FilterSelectionOption,
  MultiSelectionFilterComponent,
  ShowAllOptionsButtonComponent,
  getVisibleOptions,
} from '@core/ui';
import { ListFacetBase, MultiSelectFacet } from '@mp/shared/facets/domain';
import {
  BucketIconFn,
  FacetExpansionStateService,
  filterBuckets,
  getBucketByValueMap,
  mapFacetBucketsToOptions,
} from '@mp/shared/facets/util';

import { MultiSelectFacetBucketContext, MultiSelectFacetInsightsEventsConfig } from '../../models';

@Component({
  selector: 'mp-multi-select-facet',
  templateUrl: './multi-select-facet.component.html',
  styleUrl: './multi-select-facet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,

    FilterGroupComponent,
    FilterBadgeComponent,
    FilterClearButtonComponent,
    MultiSelectionFilterComponent,
    ShowAllOptionsButtonComponent,
    TypedTemplateDirective,
  ],
  providers: [FacetExpansionStateService],
})
export class MultiSelectFacetComponent<TData extends object | unknown = unknown> implements OnInit {
  /**
   * The MultiSelectFacet object to display.
   */
  readonly facet: InputSignal<MultiSelectFacet<TData>> = input.required<MultiSelectFacet<TData>>();

  /**
   * The multiselect facet filter selected values.
   */
  readonly selectedValues: ModelSignal<string[] | undefined> = model.required<string[] | undefined>();

  /**
   * Specifies the name of the filter.
   */
  readonly filterName: InputSignal<string> = input.required<string>();

  /**
   * Specifies the icon of the filter component.
   */
  readonly icon: InputSignal<string | undefined> = input<string>();

  /**
   * If the current selection state should be stored, provide a unique key within the current instance
   * of `FacetSelectionService`. This is a constant value and must not be changed.
   */
  readonly selectionKey: InputSignal<string | undefined> = input<string>();

  /**
   * The placeholder of the search field.
   */
  readonly searchFieldPlaceholder: InputSignal<string> = input<string>('Merkmale durchsuchen');

  /**
   * Sets the number of items visible when not expanded.
   */
  readonly maxVisibleItems: InputSignal<number | null> = input<number | null>(null);

  readonly bucketIconFn: InputSignal<BucketIconFn<TData> | undefined> = input<BucketIconFn<TData>>();

  readonly trackInsightsEvents: InputSignal<boolean> = input<boolean>(false);

  /**
   * Emits on selection change and provides the selected values of the facet.
   */
  readonly changed: OutputEmitterRef<string[]> = output<string[]>();

  /**
   * Emits on clear selection click
   */
  readonly clear: OutputEmitterRef<void> = output<void>();

  /**
   * The template to use for a bucket.
   */
  @ContentChild(TemplateRef)
  bucketTemplate?: TemplateRef<MultiSelectFacetBucketContext<TData>>;

  protected readonly searchTerm: WritableSignal<string> = signal<string>('');

  protected readonly showAll: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly filteredBuckets: Signal<readonly ListFacetBase.Bucket<TData>[]> = computed(() =>
    filterBuckets(this.facet().buckets, this.searchTerm()),
  );
  protected readonly visibleBuckets: Signal<readonly ListFacetBase.Bucket<TData>[]> = computed(() =>
    getVisibleOptions(this.filteredBuckets() as ListFacetBase.Bucket<TData>[], this.maxVisibleItems(), this.showAll()),
  );

  protected readonly filterOptions: Signal<FilterSelectionOption<string>[]> = computed(() =>
    mapFacetBucketsToOptions(this.visibleBuckets(), this.bucketIconFn()),
  );

  protected readonly selectionCount: Signal<number> = computed(() => this.selectedValues()?.length ?? 0);

  readonly bucketByValueMap: Signal<Record<string, ListFacetBase.Bucket<TData>>> = computed(() =>
    getBucketByValueMap(this.facet().buckets),
  );

  protected readonly insightsEventsConfig: Signal<MultiSelectFacetInsightsEventsConfig> = computed(() =>
    this.getFilterInsightsEventsConfig(this.filterName()),
  );

  get isExpanded(): boolean {
    return this.facetExpansionStateService.isExpanded();
  }

  get isSearchExpanded(): boolean {
    return this.facetExpansionStateService.isSearchExpanded();
  }

  constructor(
    private readonly facetExpansionStateService: FacetExpansionStateService,
    @Optional() private readonly insightsEventsTrackingService: InsightsEventsTrackingService | null,
  ) {}

  ngOnInit(): void {
    this.facetExpansionStateService.setSelectionKey(this.selectionKey());
  }

  onSelectionChanged(values: string[]): void {
    this.selectedValues.set(values);
    this.changed.emit(values);
  }

  clearSelection(): void {
    this.onSelectionChanged([]);

    this.trackInsightsEvent(this.insightsEventsConfig().filterReset);

    this.clear.emit();
  }

  onExpandedChange(expanded: boolean): void {
    this.facetExpansionStateService.setExpanded(expanded);

    const filterExpansionEvent: InsightsEvent = expanded
      ? this.insightsEventsConfig().filterExpand
      : this.insightsEventsConfig().filterCollapse;
    this.trackInsightsEvent(filterExpansionEvent);
  }

  onSearchExpandedChange(expanded: boolean): void {
    this.facetExpansionStateService.setSearchExpanded(expanded);
  }

  onSearchTermChange(searchTerm: string): void {
    this.searchTerm.set(searchTerm);

    if (searchTerm) {
      this.trackInsightsEvent(this.insightsEventsConfig().filterSearch);
    }
  }

  onBucketOptionSelect({ label }: FilterSelectionOption<string>): void {
    this.trackInsightsEvent(this.insightsEventsConfig().valueSelect, { value: label });
  }

  private trackInsightsEvent(event: InsightsEvent, customProperties?: InsightsEventProperties): void {
    if (!this.trackInsightsEvents()) {
      return;
    }

    this.insightsEventsTrackingService?.trackEvent(event, customProperties);
  }

  private getFilterInsightsEventsConfig(filterName: string): MultiSelectFacetInsightsEventsConfig {
    return {
      filterExpand: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_EXPAND),
      filterCollapse: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_COLLAPSE),
      valueSelect: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_CHANGE),
      filterReset: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_RESET),
      filterSearch: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_SEARCH),
    };
  }
}
